import { VA_FORM_IDS } from 'platform/forms/constants';
import { toggleValues } from 'platform/site-wide/feature-toggles/selectors';
import { isLoggedIn } from 'platform/user/selectors';
import { hasSession } from 'platform/user/profile/utilities';
import environment from 'platform/utilities/environment';
import TOGGLE_NAMES from 'platform/utilities/feature-toggles/featureFlagNames';

import { isExpired } from './utils';

// formId of the live (v1) 526 in-progress form.
export const V1_FORM_ID = VA_FORM_IDS.FORM_21_526EZ;
export const V2_FORM_ID = VA_FORM_IDS.FORM_21_526EZ_V2;

const hasActiveSavedForm = (savedForms, formId) =>
  savedForms.some(
    savedForm => savedForm.form === formId && !isExpired(savedForm.metadata),
  );

/**
 * Decide which 526 form version to mount: 'v1' | 'v2', or undefined while the
 * inputs (feature toggles; profile/savedForms for logged-in users) are still
 * loading — callers should wait on undefined rather than guess.
 *
 * @param {object} state Redux state
 * @returns {('v1'|'v2'|undefined)}
 */
export const selectFormVersion = state => {
  // v2 is a non-submittable scaffold in production; never mount it there.
  if (environment.isProduction()) return 'v1';

  // Toggles must be settled before any stable decision.
  if (state?.featureToggles?.loading) return undefined;

  // Logged out: default to v1 (shared intro), re-evaluated after sign in. On a
  // cold load a session can exist before login state hydrates, so wait then
  // rather than lock a returning v2 Veteran onto v1.
  if (!isLoggedIn(state)) {
    if (hasSession()) return undefined;
    return 'v1';
  }

  // Logged in: wait for the profile so savedForms are available.
  if (state?.user?.profile?.loading) return undefined;

  const savedForms = state?.user?.profile?.savedForms || [];
  // Flipper toggles are stored under their snake_case name; camel fallback for tests.
  const toggles = toggleValues(state);
  const flagOn =
    toggles[TOGGLE_NAMES.disability526AllClaimsV2] === true ||
    toggles.disability526AllClaimsV2 === true;
  const hasActiveV2 = hasActiveSavedForm(savedForms, V2_FORM_ID);
  const hasActiveV1 = hasActiveSavedForm(savedForms, V1_FORM_ID);

  if (hasActiveV2) return 'v2'; // sticky: v2 draft can't resume in v1
  if (hasActiveV1) return 'v1'; // grandfathered: v1 draft never routed to v2
  if (flagOn) return 'v2'; // enrollment: new start while flag on
  return 'v1';
};
